import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import Breadcrumbs from '../components/Breadcrumbs';
import CompactBrowserViewer from '../components/CompactBrowserViewer';
import RunRecording from '../components/RunRecording';
import IdentityInbox from '../components/IdentityInbox';
import { RunStatusBadge } from '../components/AccountStatusBadges';
import { useSente, SenteRun, SenteRegistration, SenteIdentity, SenteCredentials, SenteMessage } from '../hooks/useSente';
import { useRunStream } from '../hooks/useRunStream';

const TERMINAL = ['completed', 'failed'];
// The gateway session is attachable while the agent drives (or holds at a block).
const STREAMABLE = ['running', 'awaiting_verification', 'blocked'];

const appHost = (appUrl?: string) => {
  if (!appUrl) return '';
  try {
    return new URL(appUrl).host;
  } catch {
    return appUrl;
  }
};

const STATUS_COPY: Record<SenteRun['status'], string> = {
  queued: 'Waiting for a worker to pick this run up — usually a couple of seconds.',
  running: 'The managed browser is working. Watch it live below.',
  awaiting_verification:
    "Waiting for the verification email to land in the identity's inbox — the code or link is extracted and injected automatically.",
  blocked:
    'The browser hit something it can\'t clear on its own. Clear the block yourself in the interactive live view below, then resume. Held for ~10 minutes before failing.',
  completed: 'Done — the account is ready to use.',
  failed: 'The run failed — check the error below, then retry.',
};

// Per-code copy for the `blocked` states that are intended pauses, not failures.
const BLOCKED_COPY: Record<string, string> = {
  SUBMIT_CONFIRMATION_REQUIRED:
    'The form is filled and ready. Open the live view below, click the final submit button yourself (this accepts the app’s terms), then resume. Held for ~10 minutes.',
  MANUAL_INTERVENTION:
    'You paused the agent and have control of the live browser below. Do whatever you need — solve a challenge, adjust the page, click through — then hand control back with Resume.',
  MFA_REQUIRED:
    'The app sent a verification code to the account owner (their email or phone). Open the live view below, enter the code the owner received, then resume. Held for ~10 minutes.',
  TOTP_REQUIRED:
    'The app is asking for an authenticator (TOTP) code. If you didn’t supply a TOTP seed when connecting, enter a code from your authenticator app in the live view below, then resume. (Add the seed on the connection so future logins clear this automatically.)',
};

// Four tones drive the whole status/action area. `blocked` splits: intended pauses
// (submit-confirmation, manual take-over) read amber "needs you", real blocks read red.
type Tone = 'progress' | 'paused' | 'success' | 'failure';
const runTone = (run: SenteRun): Tone => {
  if (run.status === 'completed') return 'success';
  if (run.status === 'failed') return 'failure';
  if (run.status === 'blocked') {
    return run.error && BLOCKED_COPY[run.error.code] ? 'paused' : 'failure';
  }
  return 'progress';
};

const TONE_PANEL: Record<Tone, string> = {
  progress: 'bg-blue-50/60 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900/50',
  paused: 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/50',
  success: 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900/50',
  failure: 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900/50',
};

const TONE_DOT: Record<Tone, string> = {
  progress: 'bg-blue-500',
  paused: 'bg-amber-500',
  success: 'bg-green-500',
  failure: 'bg-red-500',
};

const RunDetailInner: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const navigate = useNavigate();
  const { ready, getRun, getRegistration, getIdentity, resumeRun, abortRun, interveneRun, getCredentials, listMessages, loginRegistration, createRegistration } = useSente();
  const [run, setRun] = useState<SenteRun | null>(null);
  const [registration, setRegistration] = useState<SenteRegistration | null>(null);
  const [identity, setIdentity] = useState<SenteIdentity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isResuming, setIsResuming] = useState(false);
  const [isAborting, setIsAborting] = useState(false);
  const [isIntervening, setIsIntervening] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  // The verification email the agent is currently applying (drives the inbox→browser hand-off beat).
  const [verifyMsg, setVerifyMsg] = useState<SenteMessage | null>(null);
  // Reveal-on-click vaulted credentials (the account payoff).
  const [creds, setCreds] = useState<SenteCredentials | null>(null);
  const [isRevealing, setIsRevealing] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  // Bumped on resume/abort so an in-flight poll response from before the mutation can't
  // clobber the fresh post-mutation run state.
  const mutationSeq = React.useRef(0);

  const load = useCallback(async () => {
    if (!runId) return;
    const seq = mutationSeq.current;
    try {
      const fresh = await getRun(runId);
      if (seq !== mutationSeq.current) return;
      setRun(fresh);
      setError(null);
    } catch (e: any) {
      if (seq !== mutationSeq.current) return;
      setError(e.message || 'Failed to load run');
    }
  }, [runId, getRun]);

  useEffect(() => {
    if (ready) load();
  }, [ready, load]);

  // Poll while the run is live — also retries a failed initial load.
  useEffect(() => {
    if (!ready || (run && TERMINAL.includes(run.status))) return;
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [ready, run, load]);

  // Registration + identity context (once per registrationId — `run` itself changes every poll).
  const registrationId = run?.registrationId;
  useEffect(() => {
    if (!ready || !registrationId || registration) return;
    getRegistration(registrationId)
      .then(reg => {
        setRegistration(reg);
        return getIdentity(reg.identityId).then(setIdentity);
      })
      .catch(() => {}); // context only — the run view stands alone
  }, [ready, registrationId, registration, getRegistration, getIdentity]);

  const streaming = Boolean(run && STREAMABLE.includes(run.status));
  const { screenshotUrl, currentUrl, isConnected } = useRunStream(run?.gatewaySessionId, streaming);

  // B2 — while the agent waits on (and applies) the verification email, surface the extracted
  // OTP/link so the inbox→browser hand-off is legible. Real annotation data, same as the inbox shows.
  const identityId = identity?.id;
  const runCreatedAt = run?.createdAt;
  const awaitingOrApplying = run?.status === 'awaiting_verification' || run?.status === 'running';
  useEffect(() => {
    if (!ready || !identityId || !awaitingOrApplying) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const msgs = await listMessages(identityId, { direction: 'inbound', limit: 8 });
        const hit = msgs.find(
          m =>
            (m.annotation?.kind === 'otp' || m.annotation?.kind === 'magic_link') &&
            (!runCreatedAt || new Date(m.createdAt) >= new Date(runCreatedAt)),
        );
        if (!cancelled && hit) setVerifyMsg(hit);
      } catch {
        /* transient — keep polling */
      }
    };
    poll();
    const t = setInterval(poll, 2500);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [ready, identityId, awaitingOrApplying, runCreatedAt, listMessages]);

  const handleReveal = async () => {
    if (!run?.registrationId) return;
    setIsRevealing(true);
    try {
      setCreds(await getCredentials(run.registrationId));
    } catch (e: any) {
      setError(e.message || 'Failed to reveal credentials');
    } finally {
      setIsRevealing(false);
    }
  };

  const copyValue = (key: string, value: string) => {
    // Guard: navigator.clipboard is undefined on insecure origins — don't flash "Copied" on failure.
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(prev => (prev === key ? null : prev)), 1500);
    }).catch(() => {});
  };

  // The stream container is present for the WHOLE live run (queued → running → blocked), so the
  // layout never reshapes as the session connects — a fixed-size placeholder holds the slot until
  // the stream (or blocked live view) is ready. Terminal runs have no view → inbox spans full width.
  const runLive = Boolean(run && !TERMINAL.includes(run.status));
  // Reserve the inbox column from the start of a live run (register/login always have a
  // registration → identity will load), so the viewer doesn't reshape when the panel appears.
  const showInbox = Boolean(identity || (runLive && run?.registrationId));

  const handleResume = async () => {
    if (!run) return;
    setIsResuming(true);
    try {
      const updated = await resumeRun(run.id);
      mutationSeq.current++;
      setRun(updated);
    } catch (e: any) {
      setError(e.message || 'Failed to resume');
    } finally {
      setIsResuming(false);
    }
  };

  // Retry a failed run — fires a fresh run and jumps to it. An active account or a connected account
  // re-logs-in (reusing vaulted credentials); a not-yet-created account re-runs the register. Mirrors
  // the identity-page Retry/Log-in semantics so a connected retry never hits 409 ALREADY_CONNECTED.
  const handleRetry = async () => {
    if (!run || !registration) return;
    setIsRetrying(true);
    try {
      const reLogin = registration.status === 'active' || registration.kind === 'connected';
      const { run: newRun } = reLogin
        ? await loginRegistration(registration.id)
        : await createRegistration({ identityId: registration.identityId, appUrl: registration.appUrl });
      navigate(`/runs/${newRun.id}`);
    } catch (e: any) {
      setError(e?.code === 'RUN_LIMIT_EXCEEDED' ? (e.message || 'Run limit reached') : e.message || 'Failed to retry');
      setIsRetrying(false);
    }
  };

  const handleAbort = async () => {
    if (!run || !window.confirm('Abort this run? The browser session will be closed.')) return;
    setIsAborting(true);
    try {
      const updated = await abortRun(run.id);
      mutationSeq.current++;
      setRun(updated);
    } catch (e: any) {
      setError(e.message || 'Failed to abort');
    } finally {
      setIsAborting(false);
    }
  };

  // Reversible pause — no confirm. The agent stops and the interactive live view stays live so the
  // operator can take over; the run comes back as blocked/MANUAL_INTERVENTION and Resume hands back.
  const handleIntervene = async () => {
    if (!run) return;
    setIsIntervening(true);
    try {
      const updated = await interveneRun(run.id);
      mutationSeq.current++;
      setRun(updated);
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to take over');
    } finally {
      setIsIntervening(false);
    }
  };

  const host = appHost(registration?.appUrl);

  return (
    <div className="h-full overflow-hidden flex flex-col bg-white dark:bg-slate-950">
      <TopBar onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} />

      <div className="flex flex-1 min-h-0 relative">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

        <div className="flex-1 overflow-y-auto relative">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Breadcrumbs
              items={[{ label: 'Activity', path: '/activity' }, { label: host || 'Run' }]}
              className="mb-6"
            />

            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
                {error}
              </div>
            )}

            {!run ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-1">
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {host || `${run.type} run`}
                    </h1>
                    <RunStatusBadge status={run.status} errorCode={run.error?.code} />
                  </div>
                  <p className="text-sm text-gray-500 dark:text-slate-400">
                    <span className="capitalize">{run.type}</span> run
                    {identity && (
                      <>
                        {' '}as <span className="font-mono">{identity.email}</span>
                      </>
                    )}
                    {registration?.username && (
                      <>
                        {' '}· user <span className="font-mono">{registration.username}</span>
                      </>
                    )}
                    {' '}· started {new Date(run.createdAt).toLocaleString()}
                    {run.endedAt && <> · ended {new Date(run.endedAt).toLocaleString()}</>}
                  </p>
                </div>

                {/* Status panel — tone-driven (progress / paused / success / failure). The dot +
                    heading + one-line copy read the state at a glance; actions sit below, weighted
                    by importance (primary Resume, secondary Take over, subtle Abort). */}
                {(() => {
                  const tone = runTone(run);
                  const paused = run.status === 'blocked' && run.error && !!BLOCKED_COPY[run.error.code];
                  const canTakeOver = run.status === 'running' || run.status === 'awaiting_verification';
                  const isSubmitConfirm = run.error?.code === 'SUBMIT_CONFIRMATION_REQUIRED';
                  const copy =
                    run.status === 'blocked' && run.error && BLOCKED_COPY[run.error.code]
                      ? BLOCKED_COPY[run.error.code]
                      : STATUS_COPY[run.status];
                  const showActions = !TERMINAL.includes(run.status);
                  return (
                    <div className={`mb-6 rounded-xl border p-5 ${TONE_PANEL[tone]}`}>
                      <div className="flex items-start gap-3">
                        <span className={`mt-1.5 flex-shrink-0 w-2 h-2 rounded-full ${TONE_DOT[tone]} ${tone === 'progress' || paused ? 'animate-pulse' : ''}`} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm leading-relaxed text-gray-700 dark:text-slate-300">{copy}</p>
                          {/* Real failures show the raw error code; intended pauses don't. */}
                          {run.error && !paused && (
                            <p className="mt-2 text-sm font-medium text-red-700 dark:text-red-400">
                              {run.error.code}
                              {run.error.detail && <span className="font-normal"> — {run.error.detail}</span>}
                            </p>
                          )}
                          {run.status === 'completed' && (
                            <p className="mt-2 text-sm text-gray-600 dark:text-slate-400">
                              <Link to="/activity" className="underline font-medium">
                                Back to Activity
                              </Link>
                            </p>
                          )}
                        </div>
                      </div>

                      {showActions && (
                        <div className="mt-4 flex flex-wrap items-center gap-2.5">
                          {/* Primary: hand control back to the agent (only while paused). */}
                          {run.status === 'blocked' && (
                            <button
                              onClick={handleResume}
                              disabled={isResuming}
                              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg font-semibold shadow-sm transition-colors text-sm"
                            >
                              {isResuming
                                ? 'Resuming…'
                                : isSubmitConfirm
                                  ? "I've submitted — resume"
                                  : run.error?.code === 'MANUAL_INTERVENTION'
                                    ? 'Done — resume the agent'
                                    : "I've cleared it — resume"}
                            </button>
                          )}
                          {/* Secondary: pause the agent and grab the live browser. */}
                          {canTakeOver && (
                            <button
                              onClick={handleIntervene}
                              disabled={isIntervening}
                              className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 text-gray-800 dark:text-slate-100 rounded-lg font-medium transition-colors text-sm"
                            >
                              {isIntervening ? 'Pausing…' : 'Take over'}
                            </button>
                          )}
                          {run.status === 'blocked' && run.liveViewUrl && (
                            <a
                              href={run.liveViewUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              Open live view in a new tab ↗
                            </a>
                          )}
                          {/* Subtle destructive: pushed to the right, low weight. */}
                          <button
                            onClick={handleAbort}
                            disabled={isAborting}
                            className="ml-auto px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 rounded-lg font-medium transition-colors text-sm"
                          >
                            {isAborting ? 'Aborting…' : 'Abort run'}
                          </button>
                        </div>
                      )}

                      {/* Failed is terminal (no showActions), but it's the one terminal state worth
                          re-firing right here instead of bouncing to Activity/the identity page. */}
                      {run.status === 'failed' && registration && (
                        <div className="mt-4">
                          <button
                            onClick={handleRetry}
                            disabled={isRetrying}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-semibold shadow-sm transition-colors text-sm"
                          >
                            {isRetrying ? 'Retrying…' : 'Retry run'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Browser view (left) + the identity's live inbox (right). While blocked, the
                    interactive live view replaces the JPEG stream (operator takeover can freeze the
                    agent's screencast). The inbox polls while the run is live so a verification
                    email appears in place. */}
                <div className="flex flex-col lg:flex-row gap-5 lg:items-start">
                  {runLive && (
                    <div className="flex-1 min-w-0">
                      {(() => {
                        const interactive = run.status === 'blocked' && run.liveViewUrl;
                        return (
                          <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white mb-3">
                            {interactive ? (
                              <>
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                                Live browser — you have control
                              </>
                            ) : (
                              <>
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                                Live browser
                              </>
                            )}
                          </h2>
                        );
                      })()}
                      {run.status === 'blocked' && run.liveViewUrl ? (
                        <iframe
                          src={run.liveViewUrl}
                          title="Interactive live view"
                          allow="clipboard-read; clipboard-write"
                          className="w-full aspect-video rounded-xl border-2 border-amber-300 dark:border-amber-700/60 shadow-sm"
                        />
                      ) : streaming && run.gatewaySessionId ? (
                        <CompactBrowserViewer
                          screenshotUrl={screenshotUrl}
                          currentUrl={currentUrl}
                          pageTitle=""
                          projectName={host || 'Sente Browser'}
                          isConnected={isConnected}
                          heightClass="aspect-video"
                        />
                      ) : (
                        // Same-size placeholder (aspect-video) so the stream swaps in without a jump.
                        <div className="w-full aspect-video flex items-center justify-center gap-3 bg-gray-50 dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 text-gray-500 dark:text-slate-400 text-sm">
                          <div className="animate-spin w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full" />
                          Starting the managed browser…
                        </div>
                      )}
                      {/* B2 — the inbox→browser verification hand-off, a compact strip below the stream. */}
                      {(run.status === 'running' || run.status === 'awaiting_verification') &&
                        verifyMsg?.annotation && (
                          <div className="mt-3 rounded-lg border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2">
                            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs">
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                              {verifyMsg.annotation.kind === 'otp' ? (
                                <>
                                  <span className="text-gray-500 dark:text-slate-400">Code</span>
                                  <span className="font-mono font-semibold text-green-700 dark:text-green-400">
                                    {verifyMsg.annotation.code}
                                  </span>
                                  <span className="text-gray-500 dark:text-slate-400">→ entering into the browser</span>
                                </>
                              ) : (
                                <span className="text-gray-500 dark:text-slate-400">Verification link → opening in the browser</span>
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  )}
                  {/* A2 + B1 — the payoff. On success, the resolved account + reveal-on-click
                      vaulted credentials, in the slot the live browser occupied. */}
                  {TERMINAL.includes(run.status) && (
                    <div className="flex-1 min-w-0 space-y-5">
                      {/* The run's recorded video (Hyperbrowser). Ready shortly after the run ends. */}
                      <div>
                        <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Run recording</h2>
                        <RunRecording runId={run.id} />
                      </div>
                      {run.status === 'completed' && (
                      <div className="rounded-xl border border-green-200 dark:border-green-800/60 bg-gradient-to-b from-green-50 to-white dark:from-green-950/20 dark:to-slate-900 p-6">
                        <div className="flex items-center gap-2 mb-1.5">
                          <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Account ready</h2>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-slate-400 mb-5 leading-relaxed">
                          {registration?.kind === 'connected' ? (
                            <>The agent is signed in to your <span className="font-medium text-gray-800 dark:text-slate-200">{host || 'app'}</span> account.
                            The credentials you supplied stay vaulted and write-only — reuse the account anytime.</>
                          ) : (
                            <>A verified account at <span className="font-medium text-gray-800 dark:text-slate-200">{host || 'the app'}</span> now
                            belongs to this identity. The credentials are vaulted — reveal them below, or reuse the account anytime.</>
                          )}
                        </p>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 mb-5">
                          <div>
                            <dt className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-0.5">App</dt>
                            <dd className="text-sm text-gray-900 dark:text-white">{host || registration?.appUrl || '—'}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-0.5">Identity email</dt>
                            <dd className="text-sm font-mono text-gray-900 dark:text-white truncate">{identity?.email || '—'}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-0.5">Username</dt>
                            <dd className="text-sm font-mono text-gray-900 dark:text-white truncate">
                              {run.result?.username || registration?.username || '—'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-0.5">Action</dt>
                            <dd className="text-sm text-gray-900 dark:text-white capitalize">{run.type}</dd>
                          </div>
                        </dl>
                        {registration?.kind === 'connected' ? (
                          // Connected-account credentials are write-only (the customer supplied them) — the
                          // API returns 403 on read, so show a note instead of a reveal button that would fail.
                          <p className="text-xs text-gray-500 dark:text-slate-400">Credentials supplied by you — stored write-only and never shown back.</p>
                        ) : creds ? (
                          <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 divide-y divide-gray-100 dark:divide-slate-700">
                            {([['Username', creds.username], ['Password', creds.password]] as const).map(([label, value]) => (
                              <div key={label} className="flex items-center gap-3 px-4 py-2.5">
                                <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-slate-500 w-20 flex-shrink-0">{label}</span>
                                <code className="text-sm font-mono text-gray-900 dark:text-white truncate flex-1">{value || '—'}</code>
                                {value && (
                                  <button
                                    onClick={() => copyValue(label, value)}
                                    className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline flex-shrink-0"
                                  >
                                    {copied === label ? 'Copied' : 'Copy'}
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <button
                            onClick={handleReveal}
                            disabled={isRevealing}
                            className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 text-gray-800 dark:text-slate-100 rounded-lg font-medium transition-colors text-sm"
                          >
                            {isRevealing ? 'Revealing…' : 'Reveal credentials'}
                          </button>
                        )}
                      </div>
                      )}
                    </div>
                  )}
                  {showInbox && (
                    <div className={(runLive || TERMINAL.includes(run.status)) ? 'w-full lg:w-[400px] flex-shrink-0' : 'flex-1 min-w-0'}>
                      <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                        Inbox
                        {identity && (
                          <span className="ml-1 text-xs font-normal text-gray-500 dark:text-slate-400 font-mono">{identity.email}</span>
                        )}
                      </h2>
                      <div className={runLive ? 'lg:max-h-[75vh] lg:overflow-y-auto lg:pr-1' : ''}>
                        {identity ? (
                          <IdentityInbox identityId={identity.id} email={identity.email} pollMs={runLive ? 4000 : 0} />
                        ) : (
                          <div className="flex items-center justify-center py-12 bg-gray-50 dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800">
                            <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full" />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Remount on runId change so no per-run state (revealed credentials, verification message,
// registration/identity) can leak from one run's page onto another's.
const RunDetail: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  return <RunDetailInner key={runId || 'none'} />;
};

export default RunDetail;
